import React, { useState, useEffect } from 'react';
import { X, BarChart3, Users, Target, DollarSign, TrendingUp, Calendar, Mail, Phone, Building, Edit, Save, ArrowRight } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const FunnelDetailsModal = ({ funnel, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [leads, setLeads] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: funnel?.name || '',
    description: funnel?.description || ''
  });

  useEffect(() => {
    if (funnel?.id && activeTab === 'leads') {
      loadLeads();
    }
    if (funnel?.id && activeTab === 'performance') {
      loadPerformance();
    }
    if (funnel?.id && activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [funnel?.id, activeTab]);

  const loadLeads = async () => {
    if (!funnel?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await funnelService?.getFunnelLeads(funnel?.id);
      if (!error) {
        setLeads(data || []);
      }
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPerformance = async () => {
    if (!funnel?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await funnelService?.getFunnelPerformance(funnel?.id);
      if (!error) {
        setPerformance(data || []);
      }
    } catch (err) {
      console.error('Error loading performance:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!funnel?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await funnelService?.getFunnelAnalytics(funnel?.id, {
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0]
      });
      if (!error) {
        setAnalytics(data || []);
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!funnel?.id) return;
    
    try {
      const { error } = await funnelService?.updateFunnel(funnel?.id, editData);
      if (!error) {
        setIsEditing(false);
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error updating funnel:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const formatPercentage = (rate) => {
    return `${(rate || 0)?.toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US')?.format(num || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'bg-green-100 text-green-800' },
      draft: { label: 'Draft', class: 'bg-gray-100 text-gray-800' },
      paused: { label: 'Paused', class: 'bg-yellow-100 text-yellow-800' },
      archived: { label: 'Archived', class: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config?.class}`}>
        {config?.label}
      </span>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'performance', label: 'Performance', icon: Target },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            {isEditing ? (
              <input
                type="text"
                value={editData?.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e?.target?.value }))}
                className="text-xl font-semibold text-gray-900 border-b border-gray-300 focus:border-blue-500 outline-none"
              />
            ) : (
              <h2 className="text-xl font-semibold text-gray-900">
                {funnel?.name || 'Funnel Details'}
              </h2>
            )}
            {getStatusBadge(funnel?.status)}
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Description */}
        {(funnel?.description || isEditing) && (
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            {isEditing ? (
              <textarea
                value={editData?.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Funnel description..."
                className="w-full text-sm text-gray-600 bg-transparent border border-gray-300 rounded px-2 py-1 focus:border-blue-500 outline-none"
                rows={2}
              />
            ) : (
              <p className="text-sm text-gray-600">
                {funnel?.description}
              </p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => {
              const IconComponent = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Conversion Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {formatPercentage(funnel?.conversion_rate)}
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-900">Total Leads</span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {formatNumber(funnel?.total_leads)}
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">Conversions</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    {formatNumber(funnel?.total_conversions)}
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-900">Revenue</span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-900">
                    {formatCurrency(funnel?.total_revenue)}
                  </div>
                </div>
              </div>

              {/* Funnel Steps */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Funnel Steps</h3>
                <div className="space-y-3">
                  {funnel?.funnel_steps?.map((step, index) => (
                    <div key={step?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{step?.name || 'Unnamed Step'}</div>
                          <div className="text-sm text-gray-600 capitalize">
                            {step?.step_type?.replace('_', ' ') || 'Unknown Type'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatPercentage(step?.conversion_rate)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatNumber(step?.total_conversions)} / {formatNumber(step?.total_visitors)}
                        </div>
                      </div>
                      {index < (funnel?.funnel_steps?.length || 0) - 1 && (
                        <ArrowRight className="w-5 h-5 text-gray-400 ml-4" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Performance Analysis</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : performance?.length > 0 ? (
                <div className="space-y-4">
                  {performance?.map((step, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{step?.step_name || 'Unknown Step'}</h4>
                        <span className="text-sm text-gray-600 capitalize">
                          {step?.step_type?.replace('_', ' ') || 'Unknown Type'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-gray-600">Visitors</div>
                          <div className="text-lg font-semibold">{formatNumber(step?.visitors)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Conversions</div>
                          <div className="text-lg font-semibold">{formatNumber(step?.conversions)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Conversion Rate</div>
                          <div className="text-lg font-semibold text-green-600">{formatPercentage(step?.conversion_rate)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Drop-off Rate</div>
                          <div className="text-lg font-semibold text-red-600">{formatPercentage(step?.drop_off_rate)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No performance data available
                </div>
              )}
            </div>
          )}

          {activeTab === 'leads' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Funnel Leads</h3>
                <div className="text-sm text-gray-600">
                  {leads?.length || 0} total leads
                </div>
              </div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : leads?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Step</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {leads?.map((lead) => (
                        <tr key={lead?.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900">
                                {lead?.first_name && lead?.last_name 
                                  ? `${lead?.first_name} ${lead?.last_name}`
                                  : lead?.email || 'Unknown'
                                }
                              </div>
                              <div className="text-sm text-gray-600 flex items-center gap-2">
                                <Mail className="w-3 h-3" />
                                {lead?.email || 'No email'}
                              </div>
                              {lead?.phone && (
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <Phone className="w-3 h-3" />
                                  {lead?.phone}
                                </div>
                              )}
                              {lead?.company && (
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <Building className="w-3 h-3" />
                                  {lead?.company}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">
                              {lead?.current_step?.name || 'No step assigned'}
                            </div>
                            <div className="text-xs text-gray-600">
                              Step {lead?.current_step?.step_order || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900 capitalize">
                              {lead?.source?.replace('_', ' ') || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">
                              {lead?.lead_score || 0}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(lead?.total_value)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              lead?.is_converted 
                                ? 'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                            }`}>
                              {lead?.is_converted ? 'Converted' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No leads found for this funnel
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Analytics (Last 30 Days)</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : analytics?.length > 0 ? (
                <div className="space-y-4">
                  {analytics?.map((record, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-600" />
                          <span className="font-medium text-gray-900">{formatDate(record?.date)}</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {record?.step?.name || 'Overall'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-gray-600">Visitors</div>
                          <div className="text-lg font-semibold">{formatNumber(record?.visitors)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Conversions</div>
                          <div className="text-lg font-semibold">{formatNumber(record?.conversions)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Conversion Rate</div>
                          <div className="text-lg font-semibold text-green-600">{formatPercentage(record?.conversion_rate)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Revenue</div>
                          <div className="text-lg font-semibold text-emerald-600">{formatCurrency(record?.revenue)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No analytics data available for the selected period
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FunnelDetailsModal;